// Dashboard drill-down links (item 4, task 2.1/2.4; design decisions 2a, 6).
//
// A widget click opens the report behind it already filtered, so the total the report
// arrives at is the figure on the widget. The deciding is the backend's: every widget
// response carries `reportId` and a `drill` object whose keys are the target report's
// column fields and whose values are already in the shapes the reports screen posts -
// ids for a dropdown, a date_modifier_func for a named period, `<field>From`/`<field>To`
// for a range - built from the values the aggregate actually ran with. That is what makes
// tile and report agree: one set of filters, not two definitions that have to match.
//
// This module only turns that into a route. It resolves the report id through the reports
// the signed-in user actually has (so a widget pointing at a report they cannot open
// offers no drill), and refuses any value outside the backend's vocabulary, so a stale or
// hand-edited widget definition cannot put arbitrary text into a link.

// The same vocabulary the backend validates at ingress (AdminServiceApplication.kt:49-50,
// appendSqlCondition / appendSqlDateCondition).
const IDS = /^\d+(,\d+)*$/
const WORD = /^[A-Za-z][A-Za-z0-9]*$/
const DATE = /^\d{4}[/-]\d{2}[/-]\d{2}$/
const NUMBER = /^-?\d+(\.\d+)?$/

const MONTHS = 12

function reportNameFor(reportId, reports) {
  if (reportId === null || reportId === undefined || reportId === '') return null
  const id = Number(reportId)
  if (!Number.isFinite(id)) return null
  const match = (reports || []).find(report => report && Number(report.id) === id)
  return match && match.reportName ? match.reportName : null
}

// One filter value, or null when it is not something the reports screen can post.
function cleanValue(key, raw) {
  if (raw === null || raw === undefined) return null
  if (typeof raw === 'boolean') return raw ? 'true' : 'false'
  const value = String(raw).trim()
  if (!value) return null

  // The report's own global search: free text, because customer names carry spaces and '&'.
  if (key === 'search') return value
  if (key === 'sortFieldId') return IDS.test(value) ? value : null
  if (key === 'sortOrder') return value === '1' || value === '-1' ? value : null

  if (/(From|To)$/.test(key)) {
    if (DATE.test(value)) return value.replace(/-/g, '/')
    return NUMBER.test(value) ? value : null
  }
  // A dropdown's ids, or a single word: a named date period, or a `*`-prefixed static list.
  return IDS.test(value) || WORD.test(value) ? value : null
}

function cleanQuery(drill) {
  const query = {}
  Object.keys(drill || {}).forEach(key => {
    const value = cleanValue(key, drill[key])
    if (value !== null) query[key] = value
  })
  return query
}

function routeTo(reportName, query) {
  return { name: 'reports', params: { id: reportName }, query }
}

// The route a widget click opens, or null when this widget has no drill.
export function drillTarget(widgetData, reports, extraQuery) {
  const data = widgetData || {}
  const reportName = reportNameFor(data.reportId, reports)
  if (!reportName) return null
  return routeTo(reportName, Object.assign(cleanQuery(data.drill), cleanQuery(extraQuery)))
}

// True when the widget has a report the user can open - so only a widget that will
// actually go somewhere is given a pointer and a click.
export function hasDrill(widgetData, reports) {
  return drillTarget(widgetData, reports) !== null
}

// A graph bar: the clicked month, on the year the graph is showing. Report 12 is
// function-backed, so its month and year are the function's own parameters and travel as
// pMonth/currYear ranges (contract §3).
export function graphDrillTarget(widgetData, month, reports) {
  const m = Number(month)
  if (!Number.isInteger(m) || m < 1 || m > MONTHS) return null
  return drillTarget(widgetData, reports, { pMonthFrom: String(m), pMonthTo: String(m) })
}

// A list row, where the widget says which of the row's fields the target report can be
// narrowed by. Report 12 has no customer filter - its search matches custName (decision
// 2a) - so TOP10CUST carries the customer as the report's search term.
export function rowDrillTarget(widgetData, row, reports) {
  const data = widgetData || {}
  const field = data.rowSearchField
  const value = field && row ? row[field] : null
  return drillTarget(data, reports, value ? { search: value } : null)
}
